import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApiClient } from './use-api-client.js';
import { useAuth } from '../auth/index.js';
import { createBlockedApi } from '../api/blocked.js';

export function useReviews(serverId: string) {
  const api = useApiClient();
  return useQuery({ queryKey: ['reviews', serverId], queryFn: () => api.reviews.list(serverId), refetchInterval: 60_000 });
}

/** Mounted only when the row is open, so it loads on demand */
export function useReview(serverId: string, externalRef: string) {
  const api = useApiClient();
  return useQuery({ queryKey: ['review', serverId, externalRef], queryFn: () => api.reviews.get(serverId, externalRef) });
}

/** Posts the proposal's reply in the PR's conversation under the operator's name (a mention answered) */
export function usePostConversationReply(serverId: string, externalRef: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api.reviews.postConversationReply(serverId, externalRef, body),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['review', serverId, externalRef] });
      void qc.invalidateQueries({ queryKey: ['reviews', serverId] });
      void qc.invalidateQueries({ queryKey: ['inbox', serverId] });
    },
  });
}

/** A review prepared on the operator's demand (ADR-039 step 2): queued; its proposal comes back in the list */
export function useStartReview(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pr: string) => api.reviews.start(serverId, pr),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['reviews', serverId] }); },
  });
}

export function useHeldEvents(serverId: string) {
  const api = useApiClient();
  return useQuery({ queryKey: ['held', serverId], queryFn: () => api.held.list(serverId), refetchInterval: 60_000 });
}

export function useResolveHeld(serverId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ heldId, action }: { heldId: string; action: 'release' | 'dismiss' }): Promise<void> => {
      if (action === 'release') await api.held.release(serverId, heldId);
      else await api.held.dismiss(serverId, heldId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['held', serverId] });
      // Trust and deliver adds the comment's author to the trusted people
      void queryClient.invalidateQueries({ queryKey: ['actors', serverId] });
    },
  });
}

function useBlockedApi() {
  const { getIdToken } = useAuth();
  return useMemo(() => createBlockedApi(getIdToken), [getIdToken]);
}

/** Object events refused for the daily budget (retried every 15 minutes by the queue) */
export function useBlockedEvents(serverId: string) {
  const api = useBlockedApi();
  return useQuery({ queryKey: ['blocked', serverId], queryFn: () => api.list(serverId), refetchInterval: 60_000 });
}

export function useRelaunchBlocked(serverId: string) {
  const api = useBlockedApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (externalRef: string) => api.relaunch(serverId, externalRef),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['blocked', serverId] });
      void queryClient.invalidateQueries({ queryKey: ['held', serverId] });
    },
  });
}
